import {lazy, Suspense, useContext, useEffect, useRef} from 'react'
import {BrowserRouter, Route, Routes, useLocation} from 'react-router-dom'
import routes from 'utils/routes'
import PrivateRoute from 'components/PrivateRoute'
import PublicRoute from 'components/PublicRoute'
import AdminRoute from 'components/AdminRoute'
import {AuthContext} from 'contexts/AuthContext'
import {getItemFromStorage} from 'utils/storage'
import Loading from 'components/Loading'
import {useMutation} from 'react-query'
import {getMyProfile} from 'api'
import HomeNavigation from 'components/HomeNavigation'
import PendingInviteRedeemer from 'components/PendingInviteRedeemer'
import ModalShell from 'components/Map/ModalShell'

// Modal-route targets reuse the same lazy chunks as the regular route
// table. Webpack dedupes by source path, so the chunk is fetched once
// regardless of which route mode triggers it. The ModalShell wraps the
// content in InsideModalProvider so Navbar/Footer self-hide; the page
// component itself is unchanged.
const SpacePageLazy = lazy(async () => await import('pages/SpacePage'))
const JobPageLazy = lazy(async () => await import('pages/JobPage'))
const ClassPageLazy = lazy(async () => await import('pages/ClassPage'))
const BusinessProfileLazy = lazy(
  async () => await import('pages/BusinessProfile')
)
// Signed-in only, so its code and art stay out of the main bundle.
const SharpetDockLazy = lazy(
  async () => await import('components/Sharpet/SharpetDock')
)

/**
 * Inner routes component — split off so it can call useLocation()
 * inside the BrowserRouter. The outer App can't because hooks need
 * the router context.
 *
 * Implements the "background location" pattern from the React Router
 * docs: when a card on /map links to e.g. /spaces/<id> with
 * `state.backgroundLocation = currentLocation`, we render the routes
 * for that backgroundLocation (so /map stays mounted) AND render the
 * detail page in a modal overlay. Closing the modal pops the URL
 * back to /map without unmounting it.
 *
 * Direct visits to /spaces/<id> (no backgroundLocation in state) hit
 * the regular route as before — the modal layer is skipped.
 */
// Routes that get NO bottom tab bar: the marketing landing page, auth and
// onboarding flows, admin, payment flows, legal pages, and focused
// create/edit flows. Everything else — including detail pages like
// profiles, jobs, classes, and spaces — keeps the tabs mounted, so the
// bar never flickers in and out while navigating. Pages must not mount
// HomeNavigation themselves.
const BOTTOM_NAV_EXCLUDED_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/callback',
  '/onboarding',
  '/assessment',
  '/admin',
  '/checkout',
  '/add-credits',
  '/credits',
  '/premium-activated',
  '/claim',
  '/join',
  '/terms-of-service',
  '/privacy-policy',
  '/create-',
  '/edit-',
]

const showBottomNav = (pathname) =>
  pathname !== '/' &&
  !BOTTOM_NAV_EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p)) &&
  // An open message thread owns the bottom edge (its composer sits there);
  // the /messages list keeps the tabs.
  !/^\/messages\/./.test(pathname)

// The tab bar is fixed, so page content needs matching bottom padding on
// mobile or footers/submit buttons sit underneath it. Discover opts out:
// it's a full-bleed surface designed for the bar to overlay it.
const contentPadding = (pathname) =>
  showBottomNav(pathname) &&
  !pathname.startsWith('/discover') &&
  // /messages sizes itself to the viewport and pads its own list for the
  // bar, so wrapper padding would just add a phantom scroll.
  !pathname.startsWith('/messages')
    ? 'pb-16 lg:pb-0'
    : ''

function AppRoutes({state}) {
  const location = useLocation()
  const backgroundLocation = location.state?.backgroundLocation || null

  return (
    <div className={contentPadding(location.pathname)}>
      {/* Completes Circle joins that started logged-out on /join/:token */}
      <PendingInviteRedeemer />
      <Routes location={backgroundLocation || location}>
        {routes.map((route, i) => {
          const key = `route-${i}`

          // Handle routes with children (nested routes)
          if (route.children) {
            if (route.admin) {
              return (
                <Route
                  key={key}
                  path={route.path}
                  element={
                    route.component ? (
                      <AdminRoute>
                        <Suspense fallback={<Loading />}>
                          <route.component />
                        </Suspense>
                      </AdminRoute>
                    ) : null
                  }
                >
                  {route.children.map((childRoute, j) => (
                    <Route
                      key={`${key}-child-${j}`}
                      path={childRoute.path}
                      index={childRoute.index}
                      element={
                        childRoute.component ? (
                          <Suspense fallback={<Loading />}>
                            <childRoute.component />
                          </Suspense>
                        ) : null
                      }
                    />
                  ))}
                </Route>
              )
            }
            if (route.protected) {
              return (
                <Route
                  key={key}
                  path={route.path}
                  element={
                    route.component ? (
                      <PrivateRoute
                        isAuth={state?.isAuth}
                        path={route.path}
                        protect={route.protected}
                      >
                        <Suspense fallback={<Loading />}>
                          <route.component />
                        </Suspense>
                      </PrivateRoute>
                    ) : null
                  }
                >
                  {route.children.map((childRoute, j) => (
                    <Route
                      key={`${key}-child-${j}`}
                      path={childRoute.path}
                      index={childRoute.index}
                      element={
                        childRoute.component ? (
                          <Suspense fallback={<Loading />}>
                            <childRoute.component />
                          </Suspense>
                        ) : null
                      }
                    />
                  ))}
                </Route>
              )
            }
            return (
              <Route
                key={key}
                path={route.path}
                element={
                  route.component ? (
                    <PublicRoute path={route.path} isAuth={state?.isAuth}>
                      <Suspense fallback={<Loading />}>
                        <route.component />
                      </Suspense>
                    </PublicRoute>
                  ) : null
                }
              >
                {route.children.map((childRoute, j) => (
                  <Route
                    key={`${key}-child-${j}`}
                    path={childRoute.path}
                    index={childRoute.index}
                    element={
                      childRoute.component ? (
                        <Suspense fallback={<Loading />}>
                          <childRoute.component />
                        </Suspense>
                      ) : null
                    }
                  />
                ))}
              </Route>
            )
          }

          if (route.full) {
            return (
              <Route
                key={key}
                path={route.path}
                element={
                  route.component ? (
                    <Suspense fallback={<Loading />}>
                      <route.component />
                    </Suspense>
                  ) : null
                }
              />
            )
          }

          if (route.protected) {
            return (
              <Route
                key={key}
                path={route.path}
                element={
                  route.component ? (
                    <PrivateRoute
                      isAuth={state?.isAuth}
                      path={route.path}
                      protect={route.protected}
                    >
                      <Suspense fallback={<Loading />}>
                        <route.component />
                      </Suspense>
                    </PrivateRoute>
                  ) : null
                }
              />
            )
          }

          return (
            <Route
              key={key}
              path={route.path}
              element={
                route.component ? (
                  <PublicRoute path={route.path} isAuth={state?.isAuth}>
                    <Suspense fallback={<Loading />}>
                      <route.component />
                    </Suspense>
                  </PublicRoute>
                ) : null
              }
            />
          )
        })}
      </Routes>

      {/* Modal layer: only mounts when the current navigation has
          `backgroundLocation` set. Same paths as the regular route
          table — the URL is the source of truth either way; this layer
          just decides whether to wrap the detail page in a ModalShell. */}
      {showBottomNav(location.pathname) && <HomeNavigation />}

      {/* The Sharpet lives wherever the app chrome does. Keyed by user so
          a different sign-in on this browser starts from its own state. */}
      {showBottomNav(location.pathname) && state?.isAuth && (
        <Suspense fallback={null}>
          <SharpetDockLazy key={state?.user?.id} />
        </Suspense>
      )}

      {backgroundLocation && (
        <Routes>
          <Route
            path="/spaces/:spaceId"
            element={
              // Public, mirroring the regular /spaces/:spaceId route — a
              // logged-out visitor can open a space from the map modal.
              <ModalShell>
                <Suspense fallback={<Loading />}>
                  <SpacePageLazy />
                </Suspense>
              </ModalShell>
            }
          />
          <Route
            path="/job/:jobId"
            element={
              <PrivateRoute isAuth={state?.isAuth} protect>
                <ModalShell>
                  <Suspense fallback={<Loading />}>
                    <JobPageLazy />
                  </Suspense>
                </ModalShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/class/:classId"
            element={
              <PrivateRoute isAuth={state?.isAuth} protect>
                <ModalShell>
                  <Suspense fallback={<Loading />}>
                    <ClassPageLazy />
                  </Suspense>
                </ModalShell>
              </PrivateRoute>
            }
          />
          <Route
            path="/business/:businessId"
            element={
              <ModalShell>
                <Suspense fallback={<Loading />}>
                  <BusinessProfileLazy />
                </Suspense>
              </ModalShell>
            }
          />
        </Routes>
      )}
    </div>
  )
}

function App() {
  const {state, dispatch} = useContext(AuthContext)

  const user = useRef(getItemFromStorage({name: 'user'}))

  useEffect(() => {
    const isAuth = user?.current ? true : false
    dispatch({...state, isAuth, user: user.current})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {mutate} = useMutation('myProfile', getMyProfile, {
    onSuccess: () => {},
  })

  useEffect(() => {
    const token = state?.user?.token
    const id = state?.user?.id
    if (token) {
      mutate(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  if (!state) return <Loading />

  return (
    <div className="App font-inter">
      <BrowserRouter>
        <div className="app-content">
          <AppRoutes state={state} />
        </div>
      </BrowserRouter>
    </div>
  )
}

export default App
